import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useUser, useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { OrganizationTransition } from '@/components/ui/OrganizationTransition';

// Organization Settings Type
export interface OrganizationSettings {
  id: string;
  organization_id: string;
  enabled_features: string[];
  max_users: number;
  max_storage_gb: number;
  max_api_calls_per_month: number;
  theme_settings: {
    primary_color: string;
    secondary_color: string;
    dark_mode_default: boolean;
  };
  logo_url?: string;
  custom_domain?: string;
  require_2fa: boolean;
  allowed_ip_ranges: string[];
  password_policy: {
    min_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_numbers: boolean;
    require_special_chars: boolean;
    max_age_days: number;
  };
  session_timeout_minutes: number;
  idle_timeout_minutes: number;
  audit_retention_days: number;
  data_retention_days: number;
  created_at: string;
  updated_at: string;
}

interface OrganizationContextType {
  currentOrganization: any;
  currentOrganizationId: string | null;
  currentOrganizationSettings: OrganizationSettings | null;
  organizations: any[];
  userProfile: any;
  userProfileId: string | null;
  loading: boolean;
  isTransitioning: boolean;
  transitionData: {
    fromOrg: string | null;
    toOrg: string | null;
  } | null;
  switchOrganization: (orgId: string) => Promise<void>;
  createPersonalOrganization: () => Promise<string | null>;
  refreshOrganizations: () => Promise<void>;
  updateOrganizationSettings: (settings: Partial<OrganizationSettings>) => Promise<boolean>;
  hasFeature: (feature: string) => boolean;
  checkLimit: (limitType: 'users' | 'storage' | 'api_calls', current: number) => boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user, isSignedIn } = useUser();
  const { organization } = useOrganization();
  const { setActive, organizationList, userMemberships } = useOrganizationList() as any;
  const { toast } = useToast();
  
  const [currentOrganization, setCurrentOrganization] = useState<any>(null);
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(null);
  const [currentOrganizationSettings, setCurrentOrganizationSettings] = useState<OrganizationSettings | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userProfileId, setUserProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionData, setTransitionData] = useState<{
    fromOrg: string | null;
    toOrg: string | null;
  } | null>(null);

  // Track Clerk org changes and initialization state
  const hasInitialized = useRef(false);
  const prevClerkOrgId = useRef<string | null>(null);
  const prevClerkOrgName = useRef<string | null>(null);
  const lastSyncedOrgId = useRef<string | null>(null); // Track last org ID synced to DB
  const failedSyncAttempts = useRef<Set<string>>(new Set()); // Track failed org sync attempts
  const settingsLoadAttempted = useRef<Set<string>>(new Set()); // Track attempted settings loads

  // Edge function invoker for org management
  const invokeOrg = async (action: string, payload: any = {}) => {
    const { data, error } = await supabase.functions.invoke('org-management', {
      body: { action, ...payload },
    });
    if (error) throw error;
    return data;
  };

  // Load user profile
  useEffect(() => {
    const init = async () => {
      if (!user?.id) {
        setUserProfile(null);
        setUserProfileId(null);
        try { localStorage.removeItem('clerk_user_id'); } catch {}
        return;
      }
      try {
        // Ensure profile and personal organization exist
        await invokeOrg('bootstrap_profile', { clerkUserId: user.id });

        const profileRes = await invokeOrg('get_profile', { clerkUserId: user.id });
        setUserProfile(profileRes?.profile ?? null);
        setUserProfileId(profileRes?.profile?.id ?? null);
        // Initialize the last synced org from the DB profile
        if (profileRes?.profile?.current_organization_id) {
          lastSyncedOrgId.current = profileRes.profile.current_organization_id;
        }

        // Persist Clerk user id for services that cannot use hooks
        try {
          localStorage.setItem('clerk_user_id', user.id);
        } catch {}
      } catch (error) {
        console.error('Error loading user profile:', error);
        toast({
          title: "Error loading profile",
          description: "Failed to load user profile data",
          variant: "destructive"
        });
      }
    };

    init();
  }, [user?.id, toast]);

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!user?.id) { 
        setOrganizations([]); 
        setLoading(false); 
        prevClerkOrgId.current = null;
        return; 
      }

      try {
        const orgsRes = await invokeOrg('get_organizations', { clerkUserId: user.id });
        let orgs = orgsRes?.organizations ?? [];
        
        // SYNC: If user has a Clerk organization that doesn't exist in Supabase, create it
        if (organization && organization.id) {
          const existsInSupabase = orgs.some((org: any) => org.clerk_org_id === organization.id);
          const hasPreviouslyFailed = failedSyncAttempts.current.has(organization.id);
          
          if (!existsInSupabase && !hasPreviouslyFailed) {
            console.log('[OrgSync] 🔄 Clerk org not found in Supabase, creating:', organization.name, organization.id);
            try {
              // Use org-management edge function to create organization with proper permissions
              const { data: createResult, error: createError } = await supabase.functions.invoke('org-management', {
                body: {
                  action: 'create_clerk_organization',
                  clerkUserId: user.id,
                  clerkOrgId: organization.id,
                  orgName: organization.name || 'Unnamed Organization',
                  orgSlug: organization.slug
                }
              });
              
              if (createError) {
                console.error('[OrgSync] ❌ Edge function error:', createError);
                throw createError;
              }
              
              if (!createResult?.success) {
                console.error('[OrgSync] ❌ Edge function returned failure:', createResult);
                throw new Error(createResult?.error || 'Unknown error creating organization');
              }
              
              console.log('[OrgSync] ✅ Organization created via edge function:', createResult.organizationId);
              
              // Use organizations list returned from edge function (avoids separate query)
              if (createResult.organizations && createResult.organizations.length > 0) {
                orgs = createResult.organizations;
                console.log('[OrgSync] 📎 Using organizations from edge function response:', orgs.length);
              } else {
                // Fallback: reload organizations if not included in response
                const refreshedOrgsRes = await invokeOrg('get_organizations', { clerkUserId: user.id });
                orgs = refreshedOrgsRes?.organizations ?? orgs;
                console.log('[OrgSync] 🔄 Fetched organizations separately:', orgs.length);
              }
              
              // Verify the new org is in the list
              const newOrgExists = orgs.some((org: any) => org.clerk_org_id === organization.id);
              console.log('[OrgSync] ✅ Clerk org synced to Supabase:', createResult.organizationId);
              console.log('[OrgSync] 🔍 New org found in list?', newOrgExists);
            } catch (syncError) {
              console.error('[OrgSync] ❌ Failed to sync Clerk org:', syncError);
              // Mark this org as failed to prevent infinite retries
              failedSyncAttempts.current.add(organization.id);
              // Continue anyway - will fall back to personal org
            }
          } else if (!existsInSupabase && hasPreviouslyFailed) {
            console.log('[OrgSync] ⏭️ Skipping sync for previously failed org:', organization.id);
          }
        }
        
        setOrganizations(orgs);

        let targetOrg: any | null = null;
        
        console.log('[OrgSync] 🎯 Selecting target org:', {
          hasClerkOrg: !!organization,
          clerkOrgId: organization?.id,
          totalOrgs: orgs.length,
          orgNames: orgs.map((o: any) => o.name),
          savedProfileOrg: userProfile?.current_organization_id
        });
        
        // Prefer Clerk org when available
        if (organization) {
          targetOrg = orgs.find((org: any) => org.clerk_org_id === organization.id) || null;
          console.log('[OrgSync] 🔎 Clerk org lookup:', {
            looking_for: organization.id,
            found: !!targetOrg,
            targetOrgName: targetOrg?.name,
            targetOrgId: targetOrg?.id
          });
        }
        
        // Fallback to saved current organization
        if (!targetOrg && userProfile?.current_organization_id) {
          targetOrg = orgs.find((org: any) => org.id === userProfile.current_organization_id) || null;
          console.log('[OrgSync] 💾 Falling back to saved profile org:', targetOrg?.name);
        }
        
        // Default to personal or first org
        if (!targetOrg && orgs.length > 0) {
          targetOrg = orgs.find((org: any) => org.name === 'Personal') || orgs[0];
          console.log('[OrgSync] 🏠 Defaulting to:', targetOrg?.name);
        }

        const clerkOrgId = organization?.id ?? null;
        const isClerkChange = hasInitialized.current && prevClerkOrgId.current !== null && clerkOrgId !== null && prevClerkOrgId.current !== clerkOrgId;

        if (isClerkChange) {
          console.log('[OrgTransition] Clerk org change detected', {
            prevClerkOrgId: prevClerkOrgId.current, newClerkOrgId: clerkOrgId,
            fromOrg: prevClerkOrgName.current || currentOrganization?.name || null,
            toOrg: organization?.name || targetOrg?.name || null
          });
        }

        // If Clerk changed org (via OrganizationSwitcher), trigger our transition before updating state
        if (isClerkChange && targetOrg && !isTransitioning) {
          setTransitionData({
            fromOrg: prevClerkOrgName.current || currentOrganization?.name || null,
            toOrg: organization?.name || targetOrg.name
          });
          setIsTransitioning(true);
          console.log('[OrgTransition] Transition started for Clerk change');
        }

        if (targetOrg) {
          setCurrentOrganization(targetOrg);
          setCurrentOrganizationId(targetOrg.id);
          // Store in localStorage for other contexts to use
          localStorage.setItem('current_organization_id', targetOrg.id);
          localStorage.setItem('current_organization_name', targetOrg.name || '');
          // CRITICAL: Store Clerk org ID for bootstrap function
          if (targetOrg.clerk_org_id) {
            localStorage.setItem('clerk_org_id', targetOrg.clerk_org_id);
          }
          // Always sync DB when org differs from last synced, or when Clerk initiated the change
          const needsDbSync = lastSyncedOrgId.current !== targetOrg.id || isClerkChange;
          if (needsDbSync) {
            await invokeOrg('switch_organization', { clerkUserId: user.id, organizationId: targetOrg.id });
            lastSyncedOrgId.current = targetOrg.id;
          }
          // Load organization settings
          await loadOrganizationSettings(targetOrg.id);
        } else {
          setCurrentOrganization(null);
          setCurrentOrganizationId(null);
          setCurrentOrganizationSettings(null);
          localStorage.removeItem('current_organization_id');
          localStorage.removeItem('current_organization_name');
          localStorage.removeItem('clerk_org_id');
        }

        // Track last seen Clerk org and mark initialization complete
        prevClerkOrgId.current = clerkOrgId;
        prevClerkOrgName.current = organization?.name ?? null;
        if (!hasInitialized.current) hasInitialized.current = true;
      } catch (error) {
        console.error('Error loading organizations:', error);
        toast({
          title: "Error loading organizations",
          description: "Failed to load organization data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, [user?.id, organization?.id, toast]);

  const switchOrganization = async (orgId: string) => {
    if (!user?.id) return;

    try {
      const targetOrg = organizations.find(org => org.id === orgId);
      if (!targetOrg) {
        throw new Error('Organization not found');
      }

      // Start transition animation
      const findClerkOrgName = (clerkOrgId?: string | null) => {
        try {
          const source: any[] = (organizationList || userMemberships || []) as any[];
          const flat = Array.isArray(source) ? source.map((m: any) => m.organization ?? m) : [];
          const match = flat.find((o: any) => o?.id === clerkOrgId);
          return match?.name ?? null;
        } catch {
          return null;
        }
      };

      const fromName = organization?.name || currentOrganization?.name || null;
      const toName = findClerkOrgName(targetOrg.clerk_org_id) || targetOrg.name;

      setTransitionData({
        fromOrg: fromName,
        toOrg: toName
      });
      setIsTransitioning(true);

      // Switch Clerk active organization without page reload
      if (setActive && targetOrg.clerk_org_id) {
        await setActive({ organization: targetOrg.clerk_org_id });
      }

      // Perform the actual organization switch (backend sync)
      await invokeOrg('switch_organization', { clerkUserId: user.id, organizationId: orgId });
      lastSyncedOrgId.current = orgId;

      setCurrentOrganization(targetOrg);
      setCurrentOrganizationId(orgId);
      // Store in localStorage for other contexts to use
      localStorage.setItem('current_organization_id', orgId);
      localStorage.setItem('current_organization_name', targetOrg.name || '');
      // CRITICAL: Store Clerk org ID for bootstrap function
      if (targetOrg.clerk_org_id) {
        localStorage.setItem('clerk_org_id', targetOrg.clerk_org_id);
      }
      
      // Load new organization settings
      await loadOrganizationSettings(orgId);

      // Note: We don't show toast here as the transition handles the UI feedback
    } catch (error) {
      console.error('Error switching organization:', error);
      
      // End transition on error
      setIsTransitioning(false);
      setTransitionData(null);
      
      toast({
        title: "Error switching organization",
        description: "Failed to switch organization",
        variant: "destructive"
      });
    }
  };


  const createPersonalOrganization = async (): Promise<string | null> => {
    if (!user?.id) return null;

    try {
      const res = await invokeOrg('ensure_personal_organization', { clerkUserId: user.id });
      await refreshOrganizations();
      return res?.organizationId ?? null;
    } catch (error) {
      console.error('Error creating personal organization:', error);
      return null;
    }
  };

  const refreshOrganizations = async () => {
    if (!user?.id) return;

    try {
      const orgsRes = await invokeOrg('get_organizations', { clerkUserId: user.id });
      setOrganizations(orgsRes?.organizations ?? []);
    } catch (error) {
      console.error('Error refreshing organizations:', error);
    }
  };

  // Load organization settings
  const loadOrganizationSettings = async (orgId: string) => {
    // DISABLED: This is causing 406 errors and triggering re-render loops
    // that cause animation jitter. The organization_settings table has schema
    // issues (JSONB columns vs TypeScript expectations) that need to be resolved.
    // For now, we simply skip loading settings entirely.
    return;
  };

  // Update organization settings
  const updateOrganizationSettings = async (settings: Partial<OrganizationSettings>): Promise<boolean> => {
    if (!currentOrganizationId || !currentOrganizationSettings) return false;

    try {
      const { error } = await supabase
        .from('organization_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString()
        })
        .eq('organization_id', currentOrganizationId);

      if (error) throw error;

      // Reload settings to get the updated data
      await loadOrganizationSettings(currentOrganizationId);
      
      toast({
        title: "Settings updated",
        description: "Organization settings have been updated successfully"
      });
      
      return true;
    } catch (error) {
      console.error('Error updating organization settings:', error);
      toast({
        title: "Error updating settings",
        description: "Failed to update organization settings",
        variant: "destructive"
      });
      return false;
    }
  };

  // Check if a feature is enabled for the current organization
  const hasFeature = (feature: string): boolean => {
    if (!currentOrganizationSettings) return false;
    return currentOrganizationSettings.enabled_features.includes(feature);
  };

  // Check if current usage is within limits
  const checkLimit = (limitType: 'users' | 'storage' | 'api_calls', current: number): boolean => {
    if (!currentOrganizationSettings) return true; // No settings means no limits
    
    switch(limitType) {
      case 'users':
        return current <= currentOrganizationSettings.max_users;
      case 'storage':
        return current <= currentOrganizationSettings.max_storage_gb;
      case 'api_calls':
        return current <= currentOrganizationSettings.max_api_calls_per_month;
      default:
        return true;
    }
  };

  const value = {
    currentOrganization,
    currentOrganizationId,
    currentOrganizationSettings,
    organizations,
    userProfile,
    userProfileId,
    loading,
    isTransitioning,
    transitionData,
    switchOrganization,
    createPersonalOrganization,
    refreshOrganizations,
    updateOrganizationSettings,
    hasFeature,
    checkLimit
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
      <OrganizationTransition
        isVisible={isTransitioning}
        fromOrg={transitionData?.fromOrg || null}
        toOrg={transitionData?.toOrg || null}
        onComplete={() => {
          setIsTransitioning(false);
          setTransitionData(null);
        }}
      />
    </OrganizationContext.Provider>
  );
}

export function useOrganizationContext() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
}